import {
  namedToOption,
  namedToOptionError,
  displayNameToOptionError,
} from 'services/helpers';
import {
  checkDeleted,
  checkListDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import { type ModifyAutomationStep } from '../actionSectionTypes';
import type { RecordSource } from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { RECORD_SOURCES } from './constants';
import type { Field } from 'pages/AutomationEngine/types';

const forApi = (step: ModifyAutomationStep) => {
  const details = { ...step.details };
  const {
    actionType,
    recordSource,
    relationshipFields,
    automationVariable,
    automations,
  } = details;

  const api = {
    details: {
      actionType,
      recordSource,
      relationshipFieldIds: relationshipFields.map(({ value }) => value),
      automationVariableName: automationVariable?.value || null,
      automationIds: automations.map(({ value }) => value),
    },
  };

  return api;
};

type ApiObject = {
  actionType: string;
  recordSource: RecordSource;
  relationshipFields: (Field & { deleted: boolean })[];
  automationVariable: {
    name: string;
    deleted: boolean;
    dataSubtype: string;
  } | null;
  automations: { id: string; name: string; deleted: boolean }[];
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const {
    actionType,
    recordSource,
    relationshipFields,
    automationVariable,
    automations,
  } = apiObject;

  if (recordSource === RECORD_SOURCES.AUTOMATION_VARIABLE) {
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      automationVariable,
      nameDescriptor,
      undefined,
      'automationVariable'
    );
  }

  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    checkListDeleted(
      messageDictionary,
      addErrorMessage,
      relationshipFields,
      displayNameDescriptor,
      messageDictionary.relatedObjectField,
      'relationshipFields',
      displayNameToOptionError,
      true
    );
  }

  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    automations,
    nameDescriptor,
    messageDictionary.automation,
    'automations',
    namedToOptionError,
    true
  );

  let relatedObject = null;
  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    const firstField = relationshipFields[0];
    if (firstField) {
      const { relation } = firstField;
      relatedObject = {
        value: relation.relatedObject,
        label: relation.relatedObjectName,
        deleted: false,
        entityName: relation.relatedEntityName,
        relation,
      };
    }
  }

  return {
    actionType,
    recordSource,
    relatedObject,
    relationshipFields: (relationshipFields || []).map((field) => ({
      value: field.id,
      label: field.displayName,
      deleted: field.deleted,
      error: field.deleted,
      data: field,
    })),
    automationVariable: automationVariable && {
      value: automationVariable.name,
      label: automationVariable.name,
      deleted: automationVariable.deleted,
      meta: { dataSubtype: { value: automationVariable.dataSubtype } },
    },
    automations: (automations || [])
      .filter(({ deleted }) => !deleted)
      .map(namedToOption),
  };
};

export const modifyAutomationDTO = {
  forApi,
  forFlow,
};
